import { useState } from "react";
import { DateTime } from "luxon";
import {
  formatDateTime,
  formatDateTimeRelative,
  formatRelativeDateShort,
} from "@/lib/utils";

/**
 * A humanized timestamp: "Heute" for today, otherwise the relative label
 * ("Gestern, 14:05 Uhr", a weekday, a date). The exact date and time is in the
 * hover title, so nothing is lost by the short label.
 *
 * `variant="clock"` shows the clock time for today ("14:05 Uhr") instead of
 * "Heute": a thread of messages needs it to tell today's messages apart.
 *
 * `variant="ago"` reads "gerade eben", "vor 1 Std." within a day and the date
 * label beyond; a click toggles to the exact date and time.
 */
export default function RelativeTime({
  date,
  className,
  variant = "day",
}: {
  date?: string | null;
  className?: string;
  variant?: "day" | "clock" | "ago";
}) {
  const [absolute, setAbsolute] = useState(false);
  if (!date) return null;
  const local = DateTime.fromISO(date, { setZone: true }).toLocal();
  const exact = formatDateTime(date);

  if (variant === "ago") {
    const withinDay = DateTime.local().diff(local, "hours").hours < 24;
    const label = absolute
      ? exact
      : withinDay
        ? formatRelativeDateShort(date, "gerade eben")
        : formatDateTimeRelative(date);
    return (
      <time dateTime={local.toISO() ?? undefined} className={className}>
        <button
          type="button"
          onClick={() => setAbsolute((value) => !value)}
          title={absolute ? undefined : formatDateTime(date, true)}
          className="cursor-pointer rounded-sm hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {label}
        </button>
      </time>
    );
  }

  const label =
    variant === "day" && local.hasSame(DateTime.local(), "day")
      ? "Heute"
      : formatDateTimeRelative(date);

  return (
    <time
      dateTime={local.toISO() ?? undefined}
      title={formatDateTime(date, true)}
      className={className}
    >
      {label}
    </time>
  );
}
